/**
 * Sistema de Armazenamento
 * Gerencia localStorage, sessionStorage, IndexedDB, cache
 */

import { BasePlatformService } from '../interfaces/base_platform_service';
import { IStorageService, StorageType, StorageOptions, StorageItem, StorageInfo } from '../interfaces/storage_service';

export class StorageSystem implements IStorageService {
  readonly serviceName = 'Storage' as const;
  isInitialized = false;
  
  private dbConnection: IDBDatabase | null = null;
  private dbName = 'LavadorProDB';
  private dbVersion = 1;
  private memoryStorage = new Map<string, StorageItem>();

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    console.log('🗄️ Inicializando Sistema de Armazenamento...');

    try {
      // Verificar disponibilidade de APIs
      this.checkStorageAvailability();
      
      // Inicializar IndexedDB se disponível
      if (this.isAvailable(StorageType.INDEXED_DB)) {
        await this.initializeIndexedDB();
      }

      this.isInitialized = true;
      console.log('✅ Sistema de Armazenamento inicializado');
    } catch (error) {
      console.error('❌ Erro ao inicializar Sistema de Armazenamento:', error);
      throw error;
    }
  }

  private checkStorageAvailability(): void {
    const availability = {
      localStorage: this.isAvailable(StorageType.LOCAL),
      sessionStorage: this.isAvailable(StorageType.SESSION),
      indexedDB: this.isAvailable(StorageType.INDEXED_DB),
      cacheStorage: this.isAvailable(StorageType.CACHE),
      memory: this.isAvailable(StorageType.MEMORY)
    };

    console.log('📋 Disponibilidade de armazenamento:', availability);
  }

  private async initializeIndexedDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        reject(new Error('Erro ao abrir IndexedDB'));
      };

      request.onsuccess = () => {
        this.dbConnection = request.result;
        console.log('✅ IndexedDB conectado');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Criar object stores se necessário
        if (!db.objectStoreNames.contains('keyValue')) {
          const store = db.createObjectStore('keyValue', { keyPath: 'key' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
        
        console.log('🔧 IndexedDB atualizado');
      };
    });
  }

  isAvailable(type: StorageType): boolean {
    switch (type) {
      case StorageType.LOCAL:
        return typeof Storage !== 'undefined' && !!window.localStorage;
      case StorageType.SESSION:
        return typeof Storage !== 'undefined' && !!window.sessionStorage;
      case StorageType.INDEXED_DB:
        return 'indexedDB' in window;
      case StorageType.CACHE:
        return 'caches' in window;
      case StorageType.MEMORY:
        return true; // Always available
      default:
        return false;
    }
  }

  getBestAvailable(preferred?: StorageType[]): StorageType {
    const priorities = preferred || [
      StorageType.LOCAL,
      StorageType.INDEXED_DB,
      StorageType.SESSION,
      StorageType.CACHE,
      StorageType.MEMORY
    ];

    for (const type of priorities) {
      if (this.isAvailable(type)) {
        return type;
      }
    }

    return StorageType.MEMORY; // Fallback
  }

  async setItem<T>(
    key: string, 
    value: T, 
    type: StorageType = StorageType.LOCAL, 
    options?: StorageOptions
  ): Promise<boolean> {
    try {
      const item: StorageItem<T> = {
        data: value,
        timestamp: Date.now(),
        ttl: options?.ttl,
        version: '1.0'
      };

      const finalKey = options?.prefix ? `${options.prefix}:${key}` : key;

      switch (type) {
        case StorageType.LOCAL:
          if (!this.isAvailable(StorageType.LOCAL)) return false;
          localStorage.setItem(finalKey, JSON.stringify(item));
          break;

        case StorageType.SESSION:
          if (!this.isAvailable(StorageType.SESSION)) return false;
          sessionStorage.setItem(finalKey, JSON.stringify(item));
          break;

        case StorageType.INDEXED_DB:
          await this.setIndexedDBItem(finalKey, item);
          break;

        case StorageType.MEMORY:
          this.memoryStorage.set(finalKey, item);
          break;

        case StorageType.CACHE:
          // Cache API implementation would go here
          console.warn('Cache storage not yet implemented');
          return false;

        default:
          throw new Error(`Tipo de storage não suportado: ${type}`);
      }

      console.log(`💾 Item salvo: ${finalKey} (${type})`);
      return true;
    } catch (error) {
      console.error(`❌ Erro ao salvar item ${key}:`, error);
      return false;
    }
  }

  async getItem<T>(key: string, type: StorageType = StorageType.LOCAL, options?: StorageOptions): Promise<T | null> {
    try {
      const finalKey = options?.prefix ? `${options.prefix}:${key}` : key;
      let item: StorageItem<T> | null = null;

      switch (type) {
        case StorageType.LOCAL:
          if (!this.isAvailable(StorageType.LOCAL)) return null;
          const localData = localStorage.getItem(finalKey);
          item = localData ? JSON.parse(localData) : null;
          break;

        case StorageType.SESSION:
          if (!this.isAvailable(StorageType.SESSION)) return null;
          const sessionData = sessionStorage.getItem(finalKey);
          item = sessionData ? JSON.parse(sessionData) : null;
          break;

        case StorageType.INDEXED_DB:
          item = await this.getIndexedDBItem<T>(finalKey);
          break;

        case StorageType.MEMORY:
          item = this.memoryStorage.get(finalKey) as StorageItem<T> || null;
          break;

        case StorageType.CACHE:
          console.warn('Cache storage not yet implemented');
          return null;

        default:
          throw new Error(`Tipo de storage não suportado: ${type}`);
      }

      if (!item) return null;

      // Verificar TTL
      if (item.ttl && Date.now() - item.timestamp > item.ttl) {
        await this.removeItem(finalKey, type, options);
        return null;
      }

      return item.data;
    } catch (error) {
      console.error(`❌ Erro ao recuperar item ${key}:`, error);
      return null;
    }
  }

  async removeItem(key: string, type: StorageType = StorageType.LOCAL, options?: StorageOptions): Promise<boolean> {
    try {
      const finalKey = options?.prefix ? `${options.prefix}:${key}` : key;

      switch (type) {
        case StorageType.LOCAL:
          if (this.isAvailable(StorageType.LOCAL)) {
            localStorage.removeItem(finalKey);
          }
          break;

        case StorageType.SESSION:
          if (this.isAvailable(StorageType.SESSION)) {
            sessionStorage.removeItem(finalKey);
          }
          break;

        case StorageType.INDEXED_DB:
          await this.removeIndexedDBItem(finalKey);
          break;

        case StorageType.MEMORY:
          this.memoryStorage.delete(finalKey);
          break;

        case StorageType.CACHE:
          console.warn('Cache storage not yet implemented');
          return false;
      }

      console.log(`🗑️ Item removido: ${finalKey} (${type})`);
      return true;
    } catch (error) {
      console.error(`❌ Erro ao remover item ${key}:`, error);
      return false;
    }
  }

  async clear(type: StorageType = StorageType.LOCAL, options?: StorageOptions): Promise<boolean> {
    try {
      switch (type) {
        case StorageType.LOCAL:
          if (this.isAvailable(StorageType.LOCAL)) {
            if (options?.prefix) {
              // Clear only items with prefix
              const keys = Object.keys(localStorage);
              keys.forEach(key => {
                if (key.startsWith(`${options.prefix}:`)) {
                  localStorage.removeItem(key);
                }
              });
            } else {
              localStorage.clear();
            }
          }
          break;

        case StorageType.SESSION:
          if (this.isAvailable(StorageType.SESSION)) {
            if (options?.prefix) {
              const keys = Object.keys(sessionStorage);
              keys.forEach(key => {
                if (key.startsWith(`${options.prefix}:`)) {
                  sessionStorage.removeItem(key);
                }
              });
            } else {
              sessionStorage.clear();
            }
          }
          break;

        case StorageType.INDEXED_DB:
          await this.clearIndexedDB();
          break;

        case StorageType.MEMORY:
          if (options?.prefix) {
            const keys = Array.from(this.memoryStorage.keys());
            keys.forEach(key => {
              if (key.startsWith(`${options.prefix}:`)) {
                this.memoryStorage.delete(key);
              }
            });
          } else {
            this.memoryStorage.clear();
          }
          break;

        case StorageType.CACHE:
          console.warn('Cache storage not yet implemented');
          return false;
      }

      console.log(`🧹 Storage limpo: ${type}`);
      return true;
    } catch (error) {
      console.error(`❌ Erro ao limpar storage ${type}:`, error);
      return false;
    }
  }

  async getKeys(type: StorageType = StorageType.LOCAL, options?: StorageOptions): Promise<string[]> {
    try {
      let keys: string[] = [];

      switch (type) {
        case StorageType.LOCAL:
          if (this.isAvailable(StorageType.LOCAL)) {
            keys = Object.keys(localStorage);
          }
          break;

        case StorageType.SESSION:
          if (this.isAvailable(StorageType.SESSION)) {
            keys = Object.keys(sessionStorage);
          }
          break;

        case StorageType.INDEXED_DB:
          keys = await this.getIndexedDBKeys();
          break;

        case StorageType.MEMORY:
          keys = Array.from(this.memoryStorage.keys());
          break;

        case StorageType.CACHE:
          console.warn('Cache storage not yet implemented');
          return [];
      }

      // Filter by prefix if provided
      if (options?.prefix) {
        keys = keys.filter(key => key.startsWith(`${options.prefix}:`));
      }

      return keys;
    } catch (error) {
      console.error(`❌ Erro ao obter chaves do storage ${type}:`, error);
      return [];
    }
  }

  async hasItem(key: string, type: StorageType = StorageType.LOCAL, options?: StorageOptions): Promise<boolean> {
    const finalKey = options?.prefix ? `${options.prefix}:${key}` : key;
    const keys = await this.getKeys(type, options);
    return keys.includes(finalKey);
  }

  async getStorageInfo(type: StorageType = StorageType.LOCAL): Promise<StorageInfo> {
    try {
      const info: StorageInfo = {
        available: this.isAvailable(type)
      };

      if (type === StorageType.LOCAL || type === StorageType.SESSION) {
        if ('storage' in navigator && 'estimate' in navigator.storage) {
          const estimate = await navigator.storage.estimate();
          info.used = estimate.usage;
          info.total = estimate.quota;
          info.remaining = (estimate.quota || 0) - (estimate.usage || 0);
        }
      }

      return info;
    } catch (error) {
      console.error(`❌ Erro ao obter info do storage ${type}:`, error);
      return { available: false };
    }
  }

  // IndexedDB helpers
  private async setIndexedDBItem<T>(key: string, item: StorageItem<T>): Promise<void> {
    if (!this.dbConnection) {
      throw new Error('IndexedDB não conectado');
    }

    return new Promise((resolve, reject) => {
      const transaction = this.dbConnection!.transaction(['keyValue'], 'readwrite');
      const store = transaction.objectStore('keyValue');
      
      const data = { key, ...item };
      const request = store.put(data);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async getIndexedDBItem<T>(key: string): Promise<StorageItem<T> | null> {
    if (!this.dbConnection) return null;

    return new Promise((resolve, reject) => {
      const transaction = this.dbConnection!.transaction(['keyValue'], 'readonly');
      const store = transaction.objectStore('keyValue');
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result;
        if (!result) {
          resolve(null);
          return;
        }

        const { key: _, ...item } = result;
        resolve(item as StorageItem<T>);
      };

      request.onerror = () => reject(request.error);
    });
  }

  private async removeIndexedDBItem(key: string): Promise<void> {
    if (!this.dbConnection) return;

    return new Promise((resolve, reject) => {
      const transaction = this.dbConnection!.transaction(['keyValue'], 'readwrite');
      const store = transaction.objectStore('keyValue');
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async clearIndexedDB(): Promise<void> {
    if (!this.dbConnection) return;

    return new Promise((resolve, reject) => {
      const transaction = this.dbConnection!.transaction(['keyValue'], 'readwrite');
      const store = transaction.objectStore('keyValue');
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async getIndexedDBKeys(): Promise<string[]> {
    if (!this.dbConnection) return [];

    return new Promise((resolve, reject) => {
      const transaction = this.dbConnection!.transaction(['keyValue'], 'readonly');
      const store = transaction.objectStore('keyValue');
      const request = store.getAllKeys();

      request.onsuccess = () => resolve(request.result as string[]);
      request.onerror = () => reject(request.error);
    });
  }

  getStatus(): any {
    return {
      serviceName: this.serviceName,
      isInitialized: this.isInitialized,
      availability: {
        localStorage: this.isAvailable(StorageType.LOCAL),
        sessionStorage: this.isAvailable(StorageType.SESSION),
        indexedDB: this.isAvailable(StorageType.INDEXED_DB),
        cacheStorage: this.isAvailable(StorageType.CACHE),
        memory: this.isAvailable(StorageType.MEMORY)
      },
      indexedDBConnected: !!this.dbConnection,
      dbName: this.dbName,
      dbVersion: this.dbVersion,
      memoryItems: this.memoryStorage.size
    };
  }

  cleanup(): void {
    if (this.dbConnection) {
      this.dbConnection.close();
      this.dbConnection = null;
      console.log('🔌 Conexão IndexedDB fechada');
    }
    
    this.memoryStorage.clear();
    this.isInitialized = false;
    console.log('🧹 StorageSystem limpo');
  }
}
